// ---------------------------------------------------------------------------
// SEC-007 Phase 3 — Consent management CQRS event emitter (runtime tier)
// ---------------------------------------------------------------------------
// Story SEC-007 (SECURITY-COMPLIANCE-ATOMIC-STORY-REGISTRY): "Consent
// management records consent source, category, timestamp and revocation, and
// gates tracking/sends."
//
// Phase 2 (LANDED) shipped the closed CQRS vocabulary in
// `packages/db/src/schema/sec-007-consent-management-event-catalog.ts`:
//   - `consent.granted@v1`
//   - `consent.revoked@v1`
//   - `consent.expired@v1`
//   - `consent.source_recorded@v1`
//
// Phase 3 (this file): pure event-factory functions that build typed,
// narrowable `Sec007ConsentEvent` values for cross-worker adoption — every
// consent recorder + revoker (api-platform consent service, cookie banner
// route, preference center, DSAR cron, lead-ad ingest, …) `import`s ONE
// source of truth instead of free-text audit log entries or per-file
// re-narrowing of the category / source / revocation-reason / expiry-trigger
// vocabularies.
//
// Mirrors the SEC-014 (#2448) / SEC-019 / AN-001 (#2414) / MKT-001 (#2419)
// / PB-006 (#2437) Phase 3 emitter pattern verbatim — pure builders, no
// I/O, no env, no DB. Caller owns the actual emission to `customer_events`
// + downstream fanout (consent-aware tracking middleware / send-enforcement
// middleware / DSAR cron / DLQ-compliance escalation).
//
// What this file IS:
//   - Four pure factory functions returning discriminated-union
//     `Sec007ConsentEvent` values, one per Phase 2 catalogued type.
//   - Boundary asserts: non-empty `tenantId` / `consentRecordId` /
//     `subjectId` / `policyVersion`, vocab tokens narrowed through Phase 2
//     catalog guards, ISO-8601 timestamps derived from `Date` instances,
//     SHA-256 hex length checks (64 chars) for hashed identifiers, and the
//     null-revokedBy semantics enforced (`dsar_erasure` /
//     `tenant_offboarded` MUST carry `revokedBy=null`).
//   - Re-export of the Phase 2 narrowing guard for boundary use.
//
// What this file is NOT:
//   - It does not POST the event to `customer_events`.
//   - It does not declare any HTTP route, DB table, or queue topic.
//   - It does not carry raw email/phone/IP/UA — payloads carry only
//     opaque `subjectId` + pre-hashed identifiers per the Phase 2 catalog
//     PII discipline.
//   - It is opt-in: this file only locks the runtime construction contract.
//
// CLAUDE.md Governance touched:
//   #1 CQRS only — domain state changes flow through these events.
//   #4 First-seen immutability — `source_recorded` MUST be emitted once per
//      grant; callers responsible for dedup.
//   #6 Atomic events — one fact per event (4 factories, 4 event types).
//   #7 Event catalog — every event type catalogued (Phase 2) before use.
// ---------------------------------------------------------------------------

import {
  isSec007ConsentCategory,
  isSec007ConsentEventType,
  isSec007ConsentSource,
  isSec007ExpiryTrigger,
  isSec007RevocationReason,
  type Sec007ConsentCategory,
  type Sec007ConsentEvent,
  type Sec007ConsentSource,
  type Sec007ExpiryTrigger,
  type Sec007RevocationReason,
} from "./sec-007-consent-management-event-catalog";

// ---------------------------------------------------------------------------
// Closed sub-vocabularies for source_recorded
// ---------------------------------------------------------------------------

/**
 * Closed catalog of coarse user-agent classes. Mirrors the Phase 2
 * `userAgentClass` field — closed so no raw UA string is logged.
 */
export const SEC_007_USER_AGENT_CLASSES = [
  "desktop",
  "mobile",
  "tablet",
  "api",
  "unknown",
] as const;

export type Sec007UserAgentClass = (typeof SEC_007_USER_AGENT_CLASSES)[number];

export function isSec007UserAgentClass(
  value: unknown,
): value is Sec007UserAgentClass {
  return (
    typeof value === "string" &&
    (SEC_007_USER_AGENT_CLASSES as readonly string[]).includes(value)
  );
}

/**
 * SHA-256 hex digest length (64 hex chars). Pre-hashed IP / email / phone
 * MUST be a 64-char lowercase hex digest — never the raw identifier.
 */
export const SEC_007_SHA256_HEX_LENGTH = 64;

/**
 * Revocation reasons that MUST carry `revokedBy=null` (no user-initiated
 * actor exists for these branches).
 */
export const SEC_007_NULL_REVOKED_BY_REASONS = [
  "dsar_erasure",
  "tenant_offboarded",
  "bounce_suppression",
  "compliance_audit",
] as const;

/**
 * Revocation reasons that may be emitted without a user actor. Complaint
 * webhooks are external signals, but operators may also record the same
 * reason manually, so `complaint_received` permits either null or an actor.
 */
export const SEC_007_NULL_REVOKED_BY_ALLOWED_REASONS = [
  ...SEC_007_NULL_REVOKED_BY_REASONS,
  "complaint_received",
] as const;

// ---------------------------------------------------------------------------
// Boundary asserts
// ---------------------------------------------------------------------------

function assertNonEmpty(value: string, fieldName: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(
      `SEC-007 emitter rejected empty ${fieldName}. Field MUST be a non-empty string.`,
    );
  }
  return value;
}

function assertNullableNonEmpty(
  value: string | null,
  fieldName: string,
): string | null {
  if (value === null) {
    return null;
  }
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(
      `SEC-007 emitter rejected ${fieldName}=${String(value)}. Field MUST be null or a non-empty string.`,
    );
  }
  return value;
}

function assertOptionalNullableNonEmpty(
  value: string | null | undefined,
  fieldName: string,
): string | null {
  if (value === undefined) {
    return null;
  }
  return assertNullableNonEmpty(value, fieldName);
}

function assertNullableSha256Hex(
  value: string | null,
  fieldName: string,
): string | null {
  if (value === null) {
    return null;
  }
  const narrowed = assertNonEmpty(value, fieldName);
  if (narrowed.length !== SEC_007_SHA256_HEX_LENGTH) {
    throw new Error(
      `SEC-007 emitter rejected ${fieldName} length=${narrowed.length}. ` +
        `Field MUST be a ${SEC_007_SHA256_HEX_LENGTH}-char hex SHA-256 digest (NEVER the raw identifier).`,
    );
  }
  if (!/^[0-9a-f]+$/.test(narrowed)) {
    throw new Error(
      `SEC-007 emitter rejected ${fieldName}. ` +
        `Field MUST be lowercase hex (0-9, a-f) — uppercase or non-hex bytes rejected.`,
    );
  }
  return narrowed;
}

function narrowCategory(value: string): Sec007ConsentCategory {
  if (!isSec007ConsentCategory(value)) {
    throw new Error(
      `SEC-007 emitter rejected unknown category "${value}". ` +
        `Must be one of the catalogued SEC_007_CONSENT_CATEGORY_VOCAB tokens.`,
    );
  }
  return value;
}

function narrowSource(value: string): Sec007ConsentSource {
  if (!isSec007ConsentSource(value)) {
    throw new Error(
      `SEC-007 emitter rejected unknown source "${value}". ` +
        `Must be one of the catalogued SEC_007_CONSENT_SOURCE_VOCAB tokens.`,
    );
  }
  return value;
}

function narrowRevocationReason(value: string): Sec007RevocationReason {
  if (!isSec007RevocationReason(value)) {
    throw new Error(
      `SEC-007 emitter rejected unknown revocation reason "${value}". ` +
        `Must be one of the catalogued SEC_007_REVOCATION_REASON_VOCAB tokens.`,
    );
  }
  return value;
}

function narrowExpiryTrigger(value: string): Sec007ExpiryTrigger {
  if (!isSec007ExpiryTrigger(value)) {
    throw new Error(
      `SEC-007 emitter rejected unknown expiry trigger "${value}". ` +
        `Must be one of the catalogued SEC_007_EXPIRY_TRIGGER_VOCAB tokens.`,
    );
  }
  return value;
}

function narrowUserAgentClass(value: string): Sec007UserAgentClass {
  if (!isSec007UserAgentClass(value)) {
    throw new Error(
      `SEC-007 emitter rejected unknown userAgentClass "${value}". ` +
        `Must be one of the catalogued SEC_007_USER_AGENT_CLASSES tokens.`,
    );
  }
  return value;
}

// ---------------------------------------------------------------------------
// Event factories — discriminated-union producers
// ---------------------------------------------------------------------------

export interface BuildSec007ConsentGrantedInput {
  tenantId: string;
  consentRecordId: string;
  subjectId: string;
  category: Sec007ConsentCategory;
  source: Sec007ConsentSource;
  grantedAt: Date;
  /** Expiry (per category retention policy). `null` = no expiry. */
  expiresAt: Date | null;
  policyVersion: string;
  /** Pre-hashed identifiers for AN-027 CAPI cross-reference. */
  subjectHashedEmail: string | null;
  subjectHashedPhone: string | null;
}

/**
 * Builds a typed `consent.granted@v1` event. Call when a subject grants
 * consent via the consent service. Audit anchor: every send / track call
 * in the Tranche D enforcement tranche MUST trace back to a granted row.
 */
export function buildSec007ConsentGrantedEvent(
  input: BuildSec007ConsentGrantedInput,
): Sec007ConsentEvent {
  if (input.expiresAt !== null && input.expiresAt <= input.grantedAt) {
    throw new Error(
      `SEC-007 emitter rejected expiresAt ≤ grantedAt. ` +
        `Invariant: expiresAt MUST be strictly after grantedAt (or null = no expiry).`,
    );
  }
  return {
    type: "consent.granted@v1",
    payload: {
      tenantId: assertNonEmpty(input.tenantId, "tenantId"),
      consentRecordId: assertNonEmpty(input.consentRecordId, "consentRecordId"),
      subjectId: assertNonEmpty(input.subjectId, "subjectId"),
      category: narrowCategory(input.category),
      source: narrowSource(input.source),
      grantedAt: input.grantedAt.toISOString(),
      expiresAt: input.expiresAt === null ? null : input.expiresAt.toISOString(),
      policyVersion: assertNonEmpty(input.policyVersion, "policyVersion"),
      subjectHashedEmail: assertNullableSha256Hex(
        input.subjectHashedEmail,
        "subjectHashedEmail",
      ),
      subjectHashedPhone: assertNullableSha256Hex(
        input.subjectHashedPhone,
        "subjectHashedPhone",
      ),
    },
  };
}

export interface BuildSec007ConsentRevokedInput {
  tenantId: string;
  consentRecordId: string;
  subjectId: string;
  category: Sec007ConsentCategory;
  reason: Sec007RevocationReason;
  /** User id that initiated revoke; `null`/omitted for external or cron branches. */
  revokedBy?: string | null;
  revokedAt: Date;
  /** Free-form audit note. NEVER PII. */
  reasonNote: string | null;
}

/**
 * Builds a typed `consent.revoked@v1` event. Call from the revoke handler
 * (user-initiated preference center, DSAR cron, lead-ad consent revocation,
 * tenant offboard). Enforces the contract: `revokedBy=null` is only valid
 * for non-user-initiated reasons.
 */
export function buildSec007ConsentRevokedEvent(
  input: BuildSec007ConsentRevokedInput,
): Sec007ConsentEvent {
  const reason = narrowRevocationReason(input.reason);
  const revokedBy = assertOptionalNullableNonEmpty(
    input.revokedBy,
    "revokedBy",
  );

  const requiresNullActor = (
    SEC_007_NULL_REVOKED_BY_REASONS as readonly string[]
  ).includes(reason);
  const allowsNullActor = (
    SEC_007_NULL_REVOKED_BY_ALLOWED_REASONS as readonly string[]
  ).includes(reason);

  if (revokedBy === null && !allowsNullActor) {
    throw new Error(
      `SEC-007 emitter rejected revokedBy=null with reason=${reason}. ` +
        `Phase 2 contract: null revokedBy is only valid for reasons ${JSON.stringify(SEC_007_NULL_REVOKED_BY_ALLOWED_REASONS)}.`,
    );
  }
  if (revokedBy !== null && requiresNullActor) {
    throw new Error(
      `SEC-007 emitter rejected revokedBy=${revokedBy} with reason=${reason}. ` +
        `Phase 2 contract: ${reason} revocations carry revokedBy=null (no user actor).`,
    );
  }

  return {
    type: "consent.revoked@v1",
    payload: {
      tenantId: assertNonEmpty(input.tenantId, "tenantId"),
      consentRecordId: assertNonEmpty(input.consentRecordId, "consentRecordId"),
      subjectId: assertNonEmpty(input.subjectId, "subjectId"),
      category: narrowCategory(input.category),
      reason,
      revokedBy,
      revokedAt: input.revokedAt.toISOString(),
      reasonNote: assertNullableNonEmpty(input.reasonNote, "reasonNote"),
    },
  };
}

export interface BuildSec007ConsentExpiredInput {
  tenantId: string;
  consentRecordId: string;
  subjectId: string;
  category: Sec007ConsentCategory;
  trigger: Sec007ExpiryTrigger;
  expiredAt: Date;
  originallyGrantedAt: Date;
}

/**
 * Builds a typed `consent.expired@v1` event. Call from the consent-retention
 * cron when the retention window passes (or policy changed, or inactivity
 * threshold hit).
 */
export function buildSec007ConsentExpiredEvent(
  input: BuildSec007ConsentExpiredInput,
): Sec007ConsentEvent {
  if (input.expiredAt < input.originallyGrantedAt) {
    throw new Error(
      `SEC-007 emitter rejected expiredAt < originallyGrantedAt. ` +
        `Invariant: expiredAt MUST be >= originallyGrantedAt.`,
    );
  }
  return {
    type: "consent.expired@v1",
    payload: {
      tenantId: assertNonEmpty(input.tenantId, "tenantId"),
      consentRecordId: assertNonEmpty(input.consentRecordId, "consentRecordId"),
      subjectId: assertNonEmpty(input.subjectId, "subjectId"),
      category: narrowCategory(input.category),
      trigger: narrowExpiryTrigger(input.trigger),
      expiredAt: input.expiredAt.toISOString(),
      originallyGrantedAt: input.originallyGrantedAt.toISOString(),
    },
  };
}

export interface BuildSec007ConsentSourceRecordedInput {
  tenantId: string;
  consentRecordId: string;
  subjectId: string;
  source: Sec007ConsentSource;
  collectionSurfaceId: string;
  /** Pre-hashed IP-at-rest (SHA-256 64-char hex). `null` for non-HTTP sources. */
  ipHashAtRest: string | null;
  userAgentClass: Sec007UserAgentClass;
  recordedAt: Date;
}

/**
 * Builds a typed `consent.source_recorded@v1` event — immutable audit
 * metadata. Caller MUST ensure first-seen-only emission per
 * `consentRecordId` (Governance Rule #4).
 */
export function buildSec007ConsentSourceRecordedEvent(
  input: BuildSec007ConsentSourceRecordedInput,
): Sec007ConsentEvent {
  return {
    type: "consent.source_recorded@v1",
    payload: {
      tenantId: assertNonEmpty(input.tenantId, "tenantId"),
      consentRecordId: assertNonEmpty(input.consentRecordId, "consentRecordId"),
      subjectId: assertNonEmpty(input.subjectId, "subjectId"),
      source: narrowSource(input.source),
      collectionSurfaceId: assertNonEmpty(
        input.collectionSurfaceId,
        "collectionSurfaceId",
      ),
      ipHashAtRest: assertNullableSha256Hex(input.ipHashAtRest, "ipHashAtRest"),
      userAgentClass: narrowUserAgentClass(input.userAgentClass),
      recordedAt: input.recordedAt.toISOString(),
    },
  };
}

// ---------------------------------------------------------------------------
// Re-export the catalog narrowing guard for boundary use at consumers
// ---------------------------------------------------------------------------

export { isSec007ConsentEventType };
